import { computed, watch, type Ref } from "vue";
import type { GeoJSONSource, Map, MapMouseEvent } from "mapbox-gl";
import type { FeatureCollection } from "geojson";
import { withValidPointPositions, zoomToClusterLeaves } from "../utils/mapZoom";
import { mapCountLabelLayout, mapCountTextField } from "../utils/mapLabel";
import {
  bindPointerCursor,
  EMPTY_FEATURE_COLLECTION,
  MAP_LABEL_COLOR,
  MAP_POINT_COLOR,
  POINT_CIRCLE_RADIUS,
  POINT_HIT_STROKE,
  removeLayersIfPresent,
  removeSourcesIfPresent,
  useAppMapStyleLayers,
} from "./useAppMapStyleLayers";
import { GROUP_CIRCLE_RADIUS, GROUP_COUNT_SIZE, useAppMapSpiderfy } from "./useAppMapSpiderfy";

const SOURCE_ID = "sites-cluster";
const LAYER_CLUSTERS = "sites-cluster-circles";
const LAYER_COUNT = "sites-cluster-count";
const LAYER_UNCLUSTERED = "sites-unclustered";

const CLUSTER_RADIUS = 44;
const CLUSTER_MAX_ZOOM = 14;
// Zoom où le clustering cesse et où les groupes de points confondus prennent le relais.
export const GROUP_MIN_ZOOM = CLUSTER_MAX_ZOOM + 1;
const CLUSTER_CIRCLE_RADIUS = 27;
const CLUSTER_COUNT_SIZE = 18;
// Les clusters se resserrent jusqu'à la taille des groupes de points, qui prennent le relais dès
// que le clustering cesse : le passage de l'un à l'autre est alors sans à-coup.
const CLUSTER_SHRINK_START_ZOOM = 11;
const clusterZoomScale = (from: number, to: number) =>
  ["interpolate", ["linear"], ["zoom"], CLUSTER_SHRINK_START_ZOOM, from, GROUP_MIN_ZOOM, to];

const UNCLUSTERED_BASE_FILTER = ["!", ["has", "point_count"]] as const;

interface UseAppMapClusterOptions {
  data: Ref<FeatureCollection | undefined>;
  activeFeatureId?: Ref<number | string | undefined>;
  onFeatureClick?: (payload: { feature: GeoJSON.Feature; event: MapMouseEvent }) => void;
  countProperty?: string;
  localGlyphs?: Ref<boolean>;
}

export function useAppMapCluster(map: Ref<Map | undefined>, options: UseAppMapClusterOptions) {
  let previousProjection: string | undefined;

  const validData = computed(() => withValidPointPositions(options.data.value ?? EMPTY_FEATURE_COLLECTION));

  // Le clustering opère au niveau de la source : en retirer un point est le seul moyen de le sortir
  // des clusters (et de leur compteur) comme de la couche des points isolés. Sont concernés ceux
  // qu'un marqueur rend déjà ailleurs — le point actif, épinglé par AppMap depuis la collection
  // complète, et les points éclatés, portés par les pattes de l'escargot.
  function dataWithoutPinnedFeatures(): FeatureCollection {
    const data = validData.value;
    const activeId = options.activeFeatureId?.value;
    const spiderfied = spiderfy.spiderfiedIds.value;
    if (activeId == null && !spiderfied.size) return data;
    return {
      ...data,
      features: data.features.filter((f) => {
        const id = String(f.properties?.id);
        return (activeId == null || id !== String(activeId)) && !spiderfied.has(id);
      }),
    };
  }

  function applyData(m: Map) {
    const source = m.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    if (!source) return;
    source.setData(dataWithoutPinnedFeatures());
  }

  function setup(m: Map) {
    previousProjection = m.getProjection()?.name ?? "globe";
    m.setProjection("mercator");
    const countProperty = options.countProperty;
    m.addSource(SOURCE_ID, {
      type: "geojson",
      data: dataWithoutPinnedFeatures(),
      cluster: true,
      clusterRadius: CLUSTER_RADIUS,
      clusterMaxZoom: CLUSTER_MAX_ZOOM,
      ...(countProperty && { clusterProperties: { [countProperty]: ["+", ["coalesce", ["get", countProperty], 1]] } }),
    });

    m.addLayer({
      id: LAYER_UNCLUSTERED,
      type: "circle",
      source: SOURCE_ID,
      filter: UNCLUSTERED_BASE_FILTER as never,
      paint: {
        "circle-color": MAP_POINT_COLOR,
        "circle-radius": POINT_CIRCLE_RADIUS,
        "circle-stroke-width": POINT_HIT_STROKE,
        "circle-stroke-opacity": 0,
      },
    });

    m.addLayer({
      id: LAYER_CLUSTERS,
      type: "circle",
      source: SOURCE_ID,
      filter: ["has", "point_count"],
      paint: {
        "circle-color": MAP_POINT_COLOR,
        "circle-radius": clusterZoomScale(CLUSTER_CIRCLE_RADIUS, GROUP_CIRCLE_RADIUS) as never,
        "circle-stroke-width": 0,
      },
    });

    m.addLayer({
      id: LAYER_COUNT,
      type: "symbol",
      source: SOURCE_ID,
      filter: ["has", "point_count"],
      layout: mapCountLabelLayout({
        localGlyphs: options.localGlyphs?.value ?? false,
        textField: mapCountTextField(countProperty),
        textSize: clusterZoomScale(CLUSTER_COUNT_SIZE, GROUP_COUNT_SIZE) as never,
      }),
      paint: {
        "text-color": MAP_LABEL_COLOR,
      },
    });

    const onClusterClick = (e: MapMouseEvent) => {
      const feature = m.queryRenderedFeatures(e.point, { layers: [LAYER_CLUSTERS] })[0];
      if (!feature) return;
      const source = m.getSource(SOURCE_ID) as GeoJSONSource;
      zoomToClusterLeaves(m, source, feature as GeoJSON.Feature, CLUSTER_MAX_ZOOM + 1);
    };

    const onPointClick = (e: MapMouseEvent) => {
      if (spiderfy.hitsSpiderfyLayer(e.point)) return;
      const features = m.queryRenderedFeatures(e.point, { layers: [LAYER_UNCLUSTERED] });
      if (!features.length) return;
      options.onFeatureClick?.({ feature: features[0] as GeoJSON.Feature, event: e });
    };

    const unbindCursor = bindPointerCursor(m, LAYER_CLUSTERS, LAYER_UNCLUSTERED);
    m.on("click", LAYER_CLUSTERS, onClusterClick);
    m.on("click", LAYER_UNCLUSTERED, onPointClick);

    return () => {
      unbindCursor();
      m.off("click", LAYER_CLUSTERS, onClusterClick);
      m.off("click", LAYER_UNCLUSTERED, onPointClick);
      removeLayersIfPresent(m, LAYER_COUNT, LAYER_CLUSTERS, LAYER_UNCLUSTERED);
      removeSourcesIfPresent(m, SOURCE_ID);
      if (previousProjection) {
        m.setProjection(previousProjection);
      }
    };
  }

  useAppMapStyleLayers(map, setup);

  // Déclaré après le cluster pour que ses calques (groupes, escargot) se posent au-dessus.
  const spiderfy = useAppMapSpiderfy(map, {
    data: options.data,
    activeFeatureId: options.activeFeatureId,
    onFeatureClick: options.onFeatureClick,
    countProperty: options.countProperty,
    localGlyphs: options.localGlyphs,
    minZoom: GROUP_MIN_ZOOM,
  });

  watch([validData, spiderfy.spiderfiedIds, () => options.activeFeatureId?.value], () => {
    if (map.value) applyData(map.value);
  });

  return { spiderfiedPositions: spiderfy.spiderfiedPositions };
}
